import { ZodError, type z, type ZodType } from "zod";
import {
  ApiError,
  routePath,
  routes,
  type ErrorCode,
  type ReasonCode,
  type RouteBody,
  type RouteName,
  type RouteResponse,
  type Routes,
  type RouteSpec,
} from "@ai-platform/contracts";

export const API_BASE = import.meta.env.VITE_API_BASE ?? "/api";

/** The DC-API-Gateway on the portal host (ADR-057): texts from dataCycle, read only. */
export const GATEWAY_BASE = import.meta.env.VITE_GATEWAY_BASE ?? "/dc";

/** An answer outside 2xx, in the one error shape of the API (07 K-04). */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: ErrorCode;
  /** code of the permission function on 403 and 409; the portal shows its text (K-04) */
  readonly reason: ReasonCode | undefined;
  readonly details: unknown;

  constructor(status: number, error: ApiError["error"]) {
    super(error.message);
    this.name = "ApiRequestError";
    this.status = status;
    this.code = error.code;
    this.reason = error.reason;
    this.details = error.details;
  }
}

/** The gateway answered outside 2xx; it has no error shape of its own we could read. */
export class GatewayError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(`gateway answered ${status}`);
    this.name = "GatewayError";
    this.status = status;
  }
}

/** Raised on 401; the query client turns it into "no session" and the login (ADR-076). */
export class UnauthenticatedError extends ApiRequestError {
  constructor(error: ApiError["error"]) {
    super(401, error);
    this.name = "UnauthenticatedError";
  }
}

/**
 * The portal's own query or body broke the route's contract, so nothing was sent: the API would
 * only refuse it with `400 validation_failed`, and a second try sends the same again (GP2). Kept
 * apart from a `ZodError` of an answer, which is the API's breach. `cause` holds the `ZodError`.
 */
export class InvalidInputError extends Error {
  readonly route: RouteName;

  constructor(route: RouteName, cause: ZodError) {
    super(`the input of route "${route}" is outside its contract`, { cause });
    this.name = "InvalidInputError";
    this.route = route;
  }
}

/** Runs the check of a route's input; a `ZodError` becomes an `InvalidInputError` of that route. */
function checkInput<T>(route: RouteName, check: () => T): T {
  try {
    return check();
  } catch (error) {
    if (error instanceof ZodError) throw new InvalidInputError(route, error);
    throw error;
  }
}

async function toError(response: Response): Promise<ApiRequestError> {
  const parsed = ApiError.safeParse(await response.json().catch(() => null));
  const error: ApiError["error"] = parsed.success
    ? parsed.data.error
    : { code: response.status === 401 ? "unauthenticated" : "internal", message: response.statusText };
  return response.status === 401 ? new UnauthenticatedError(error) : new ApiRequestError(response.status, error);
}

/** Same-origin fetch with the session cookie; every answer outside 2xx becomes an error. */
async function send(path: string, init: RequestInit): Promise<Response> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: "same-origin",
    headers: {
      accept: "application/json",
      ...(init.body ? { "content-type": "application/json" } : {}),
      ...init.headers,
    },
  });
  if (!response.ok) throw await toError(response);
  return response;
}

/**
 * The low-level call: `path` below the API base, the answer parsed with its contract schema. A
 * response that does not match is a bug on one side and fails loudly instead of rendering
 * garbage (07 K-02).
 */
export async function apiFetch<T>(schema: ZodType<T>, path: string, init: RequestInit = {}): Promise<T> {
  const response = await send(path, init);
  return schema.parse(await response.json());
}

type RouteQueryInput<N extends RouteName> = Routes[N] extends { query: infer Q extends z.ZodType } ? z.input<Q> : never;

/** What a call of route `N` takes: path parameters, query and body as its contract defines them. */
export type RouteCall<N extends RouteName> = {
  params?: Record<string, string>;
  query?: RouteQueryInput<N>;
  body?: RouteBody<N>;
  signal?: AbortSignal;
};

function queryString(route: RouteSpec, query: unknown): string {
  if (!route.query) return "";
  const parsed = route.query.parse(query ?? {}) as Record<string, unknown>;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(parsed)) {
    if (value !== undefined && value !== null) search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

/**
 * The one read outside the API (07 §5, ADR-048): a `POST` of `body` to `path` below the
 * gateway base, the answer read with `read` from its contract. Without cookies: the gateway
 * reads dataCycle as its own technical user and never needs the portal session. An answer
 * outside 2xx throws `GatewayError`; a body that is not JSON reaches `read` as `null`.
 */
export async function gatewayPost<T>(
  read: (value: unknown) => T,
  path: string,
  body: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const init: RequestInit = {
    method: "POST",
    credentials: "omit",
    headers: { accept: "application/json", "content-type": "application/json" },
    body: JSON.stringify(body),
  };
  if (signal) init.signal = signal;
  const response = await fetch(`${GATEWAY_BASE}${path}`, init);
  if (!response.ok) throw new GatewayError(response.status);
  return read(await response.json().catch(() => null));
}

/**
 * Calls a route of the catalogue (07 §2.2) by name: path from `routes.ts`, query and body
 * checked with the route's schemas before sending, the answer parsed with its response schema.
 * Routes without a response schema answer `204` and resolve to `undefined`. A query or body
 * outside its schema throws `InvalidInputError` without sending; an answer outside its schema
 * throws the `ZodError`.
 */
export async function callRoute<N extends RouteName>(name: N, call: RouteCall<N> = {}): Promise<RouteResponse<N>> {
  const route: RouteSpec = routes[name];
  const path =
    routePath(name, call.params).replace(/^\/api/, "") + checkInput(name, () => queryString(route, call.query));
  const init: RequestInit = { method: route.method };
  if (call.signal) init.signal = call.signal;
  const body = route.body;
  if (body) init.body = JSON.stringify(checkInput(name, () => body.parse(call.body)));
  if (!route.response) {
    await send(path, init);
    return undefined as RouteResponse<N>;
  }
  return (await apiFetch(route.response, path, init)) as RouteResponse<N>;
}
